import { model, net, ui } from './state.js';
import { clamp } from '../utils/utils.js';

// The offset EMA below tracks snapshot arrivals, so it inherits their network
// jitter. Rendering samples the timeline every frame; feeding it the raw EMA
// makes every sampled pose wobble with the connection. The displayed clock
// therefore chases the EMA target at a bounded slew rate and only jumps on a
// genuine resync, so the render timeline stays continuous.
const CLOCK_SLEW_MAX_MS_PER_SEC = 25;
const CLOCK_SNAP_ERROR_MS = 250;
let displayedClockOffsetMs = null;
let lastClockSlewAtMs = 0;

// A frame is *displayed* at the vsync the animation callback was handed, but
// the callback itself runs some variable number of milliseconds later, and
// Date.now() is only millisecond-grained. Sampling poses with Date.now() inside
// the callback therefore spaces them unevenly in time even though they are
// shown at even intervals -- the head is drawn slightly ahead or behind where
// that instant deserves, which reads as shimmer. The frame timestamp has
// neither problem, so rendering runs off it, converted into the Date.now()
// domain the server clock offset lives in. Modern browsers provide the exact
// conversion via performance.timeOrigin; the EMA remains as a compatibility
// path for engines whose clocks do not share that origin.
const FRAME_CLOCK_OFFSET_EMA = 0.01;
const FRAME_CLOCK_SNAP_MS = 200;
const FRAME_CLOCK_ORIGIN_TOLERANCE_MS = 16;
let frameClockOffsetMs = null;
let frameNowMs = 0;
let frameClockActive = false;

// Modern browsers define rAF timestamps in the same monotonic clock as
// performance.now(). Converting with timeOrigin is exact and, unlike sampling
// Date.now() when the callback runs, cannot inherit callback queueing noise.
// The clock-consistency guard keeps the legacy EMA path for older or unusual
// engines whose timestamp origin does not match performance.now().
function exactFrameEpochMs(
  frameTimestampMs,
  wallNowMs,
  performanceTimeOriginMs,
  performanceNowMs
) {
  const timestamp = Number(frameTimestampMs);
  const wallNow = Number(wallNowMs);
  const timeOrigin = Number(performanceTimeOriginMs);
  const performanceNow = Number(performanceNowMs);
  if (
    !Number.isFinite(timestamp)
    || timestamp < 0
    || !Number.isFinite(wallNow)
    || !Number.isFinite(timeOrigin)
    || timeOrigin <= 0
    || !Number.isFinite(performanceNow)
  ) {
    return null;
  }
  const observedOrigin = wallNow - performanceNow;
  if (Math.abs(observedOrigin - timeOrigin) > FRAME_CLOCK_ORIGIN_TOLERANCE_MS) return null;
  return timeOrigin + timestamp;
}

// Marks the start of an animation frame. `frameTimestampMs` is the
// DOMHighResTimeStamp the browser passes to the callback.
function beginRenderFrame(frameTimestampMs) {
  const timestamp = Number(frameTimestampMs);
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    frameClockActive = false;
    return;
  }
  const wallNow = Date.now();
  const exactEpochMs = exactFrameEpochMs(
    timestamp,
    wallNow,
    globalThis.performance?.timeOrigin,
    globalThis.performance?.now?.()
  );
  if (exactEpochMs !== null) {
    frameClockOffsetMs = exactEpochMs - timestamp;
    frameNowMs = exactEpochMs;
    frameClockActive = true;
    return;
  }
  const sample = wallNow - timestamp;
  if (frameClockOffsetMs === null || Math.abs(sample - frameClockOffsetMs) > FRAME_CLOCK_SNAP_MS) {
    frameClockOffsetMs = sample;
  } else {
    frameClockOffsetMs += (sample - frameClockOffsetMs) * FRAME_CLOCK_OFFSET_EMA;
  }
  frameNowMs = timestamp + frameClockOffsetMs;
  frameClockActive = true;
}

// Ends the frame, so work driven by input or network edges between frames
// reads the real current time instead of the last displayed one.
function endRenderFrame() {
  frameClockActive = false;
}

// "Now" for anything whose output is a rendered pose: the displayed instant
// during a frame, wall-clock otherwise.
function renderNowMs() {
  return frameClockActive ? frameNowMs : Date.now();
}

function syncServerClock(serverNow) {
  if (!Number.isFinite(serverNow)) return;
  const sampleOffset = serverNow - Date.now();
  if (!ui.clockSynced) {
    ui.serverClockOffsetMs = sampleOffset;
    ui.clockSynced = true;
    return;
  }
  if (Math.abs(sampleOffset - ui.serverClockOffsetMs) > 1200) {
    ui.serverClockOffsetMs = sampleOffset;
    return;
  }
  ui.serverClockOffsetMs = ui.serverClockOffsetMs * 0.85 + sampleOffset * 0.15;
}

function estimatedOneWayMs() {
  const rtt = Number(net.rttMs);
  if (!Number.isFinite(rtt) || rtt <= 0) return 0;
  return clamp(rtt * 0.5, 0, 400);
}

function serverClockNowMs() {
  const baseOffset = ui.clockSynced ? ui.serverClockOffsetMs : 0;
  return Date.now() + baseOffset;
}

function serverNowMs() {
  const now = renderNowMs();
  const targetOffset = (ui.clockSynced ? ui.serverClockOffsetMs : 0) + estimatedOneWayMs();
  if (displayedClockOffsetMs === null) {
    displayedClockOffsetMs = targetOffset;
    lastClockSlewAtMs = now;
    return now + displayedClockOffsetMs;
  }
  const error = targetOffset - displayedClockOffsetMs;
  if (Math.abs(error) > CLOCK_SNAP_ERROR_MS) {
    displayedClockOffsetMs = targetOffset;
  } else if (error !== 0) {
    const budget = Math.max(0, now - lastClockSlewAtMs) * (CLOCK_SLEW_MAX_MS_PER_SEC / 1000);
    displayedClockOffsetMs += clamp(error, -budget, budget);
  }
  lastClockSlewAtMs = now;
  return now + displayedClockOffsetMs;
}

function stateTickNow(nowMs = serverNowMs()) {
  const serverNow = Number(model.state.serverNow);
  const baseTick = Number(model.state.tick) || 0;
  const tickMs = Math.max(1, Number(model.config.tickMs) || 16);
  if (!Number.isFinite(serverNow)) return baseTick;
  const delta = (nowMs - serverNow) / tickMs;
  return baseTick + Math.max(0, delta);
}

export {
  exactFrameEpochMs,
  beginRenderFrame,
  endRenderFrame,
  renderNowMs,
  syncServerClock,
  estimatedOneWayMs,
  serverClockNowMs,
  serverNowMs,
  stateTickNow
};
